/**
 * Resolve the commitment id the ReliablyME API expects (Mongo ObjectId or
 * legacy numeric string). Never use the UI-only `id` from transformMyCommitment
 * (parseInt slice / Math.random) — those cause 500s on getFullCommitmentDetails
 * and update/commitment/:id.
 */

const MONGO_OBJECT_ID = /^[a-f0-9]{24}$/i;

/** Strip composite id suffixes (e.g. "foo~bar" → "bar"). */
export const normalizeApiId = (value: unknown): string => {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  return raw.includes("~") ? raw.split("~").pop() || "" : raw;
};

/** True when this value is safe to pass to commitment API routes. */
export function isValidCommitmentApiId(value: unknown): boolean {
  const v = normalizeApiId(value);
  if (!v) return false;
  // UI list keys use Math.random() — must never hit the API.
  if (v.includes(".")) return false;
  if (MONGO_OBJECT_ID.test(v)) return true;
  if (/^\d+$/.test(v)) return true;
  return false;
}

/**
 * Best-effort API commitment id from a list row, notification, or context item.
 */
export function resolveCommitmentApiId(commitment: unknown): string {
  if (!commitment || typeof commitment !== "object") return "";

  const c = commitment as Record<string, unknown>;
  const extra =
    c.extraData && typeof c.extraData === "object"
      ? (c.extraData as Record<string, unknown>)
      : null;

  const candidates: unknown[] = [
    c._id,
    c.commitmentId,
    c.commitment_id,
    extra?._id,
    extra?.commitmentId,
    // `id` last — often the synthetic UI key
    c.id,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeApiId(candidate);
    if (isValidCommitmentApiId(normalized)) {
      return normalized;
    }
  }

  return "";
}

/** Stable numeric key for React lists (not for API calls). */
export function stableCommitmentListKey(apiId: string): number {
  let h = 0;
  for (let i = 0; i < apiId.length; i++) {
    h = (Math.imul(31, h) + apiId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) || 1;
}
